import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Add,
  ArrowBack,
  Close,
  Download,
  FitScreen,
  Remove,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from '@mui/material';
import mammoth from 'mammoth';
import sanitizeHtml from 'sanitize-html';
import { useEffect, useRef, useState } from 'react';
import {
  GetMitigationPlanDownloadUrlMutation,
  useGetMitigationPlanDownloadUrlMutation,
} from 'src/graphql';

type FileType = 'image' | 'pdf' | 'docx' | 'unknown';

interface Props {
  open: boolean;
  filename: string;
  fileExtension: string | null;
  url: string;
  uploadId: string;
  planId: string;
  onClose: () => void;
  onBack: () => void;
}

function detectFileType(ext: string | null): FileType {
  if (!ext) return 'unknown';
  const lower = ext.replace(/^\./, '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(lower))
    return 'image';
  if (lower === 'pdf') return 'pdf';
  if (lower === 'docx') return 'docx';
  return 'unknown';
}

export const sanitizeDocxHtml = (html: string): string =>
  sanitizeHtml(html, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'b',
      'i',
      'ol',
      'ul',
      'li',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'img',
      'span',
      'div',
    ],
    allowedAttributes: {
      a: ['href', 'target', 'rel'],
      img: ['src', 'alt', 'width', 'height'],
      '*': ['class'],
    },
    allowedSchemes: ['http', 'https'],
    allowedSchemesByTag: {
      img: ['http', 'https', 'data'],
    },
  });

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;

export const MitigationPlanFileViewer = ({
  open,
  filename,
  fileExtension,
  url,
  uploadId,
  planId,
  onClose,
  onBack,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const getDownloadURL = useGetMitigationPlanDownloadUrlMutation();

  const fileType = detectFileType(fileExtension);
  const [zoom, setZoom] = useState(1);
  const [zoomInput, setZoomInput] = useState('100');
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState(false);
  const zoomInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setZoom(1);
    setZoomInput('100');
    setDocxHtml(null);
    setDocxError(false);

    if (fileType !== 'docx' || !url) return;

    setDocxLoading(true);
    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => mammoth.convertToHtml({ arrayBuffer: buffer }))
      .then(({ value }) => {
        setDocxHtml(sanitizeDocxHtml(value));
        setDocxLoading(false);
      })
      .catch(() => {
        setDocxError(true);
        setDocxLoading(false);
      });
  }, [open, url, fileType]);

  const applyZoom = (next: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    setZoom(clamped);
    setZoomInput(String(Math.round(clamped * 100)));
  };

  const handleZoomInputBlur = () => {
    const parsed = parseInt(zoomInput, 10);
    if (!isNaN(parsed)) {
      applyZoom(parsed / 100);
    } else {
      setZoomInput(String(Math.round(zoom * 100)));
    }
  };

  const handleDownload = () => {
    getDownloadURL
      .mutateAsync({ uploadId, planId })
      .then(
        ({
          getMitigationPlanDownloadURL: { url: downloadUrl },
        }: GetMitigationPlanDownloadUrlMutation) => {
          window.location.href = downloadUrl;
        },
      )
      .catch((err: unknown) => {
        console.error('MitigationPlanFileViewer: failed to get download URL', {
          uploadId,
          planId,
          err,
        });
      });
  };

  const showZoomControls = fileType === 'image' || fileType === 'docx';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      data-testid="mitigation-plan-file-viewer"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        '& .MuiDrawer-paper': {
          width: '567px',
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${color.ui.divider.default.value}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="back"
            data-testid="file-viewer-back"
            sx={{ color: color.type.default.value }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ color: color.type.default.value }}
          >
            {filename}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="close"
          data-testid="file-viewer-close"
          sx={{ color: color.action.secondary.onSecondary.value }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          padding: '8px 16px',
          borderBottom: `1px solid ${color.ui.divider.default.value}`,
          flexShrink: 0,
        }}
      >
        {showZoomControls && (
          <>
            <IconButton
              size="small"
              onClick={() => applyZoom(zoom - ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}
              aria-label="zoom out"
            >
              <Remove fontSize="small" />
            </IconButton>
            <InputBase
              inputRef={zoomInputRef}
              value={zoomInput}
              onChange={(e) => setZoomInput(e.target.value)}
              onBlur={handleZoomInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') zoomInputRef.current?.blur();
              }}
              inputProps={{ 'aria-label': 'zoom percentage' }}
              endAdornment={
                <Typography variant="caption" sx={{ ml: '2px' }}>
                  %
                </Typography>
              }
              sx={{
                width: '64px',
                border: `1px solid ${color.ui.divider.default.value}`,
                borderRadius: tokens.semantic.radius.sm.value,
                padding: '2px 8px',
                fontSize: '14px',
                '& input': { textAlign: 'center', padding: 0 },
              }}
            />
            <IconButton
              size="small"
              onClick={() => applyZoom(zoom + ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}
              aria-label="zoom in"
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => applyZoom(1)}
              aria-label="fit to screen"
            >
              <FitScreen fontSize="small" />
            </IconButton>
          </>
        )}
        <IconButton
          size="small"
          onClick={handleDownload}
          aria-label="download"
          data-testid="file-viewer-download"
        >
          <Download fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: color.surface.variant.value,
          display: 'flex',
          justifyContent: 'center',
          alignItems: fileType === 'image' ? 'center' : 'flex-start',
          padding: '16px',
        }}
      >
        {fileType === 'image' && (
          <img
            src={url}
            alt={filename}
            style={{
              maxWidth: '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              display: 'block',
            }}
          />
        )}

        {fileType === 'pdf' && (
          <Box
            component="iframe"
            src={url}
            title={filename}
            sx={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#fff',
            }}
          />
        )}

        {fileType === 'docx' && (
          <>
            {docxLoading && <CircularProgress size={32} />}
            {docxError && (
              <FallbackView
                filename={filename}
                onDownload={handleDownload}
                tokens={tokens}
              />
            )}
            {docxHtml && !docxLoading && (
              <Box
                sx={{
                  backgroundColor: '#fff',
                  padding: '32px 40px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  width: '100%',
                  transformOrigin: 'top center',
                  transform: `scale(${zoom})`,
                  '& *': { fontFamily: 'Plus Jakarta Sans, sans-serif' },
                }}
                dangerouslySetInnerHTML={{ __html: docxHtml }}
              />
            )}
          </>
        )}

        {fileType === 'unknown' && (
          <FallbackView
            filename={filename}
            onDownload={handleDownload}
            tokens={tokens}
          />
        )}
      </Box>
    </Drawer>
  );
};

interface FallbackViewProps {
  filename: string;
  onDownload: () => void;
  tokens: LensTheme['tokens'];
}

const FallbackView = ({ filename, onDownload, tokens }: FallbackViewProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      padding: '32px',
      textAlign: 'center',
    }}
  >
    <Typography variant="body2" color={tokens.semantic.color.type.muted.value}>
      Preview not available for <strong>{filename}</strong>
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
      onClick={onDownload}
    >
      Download file
    </Typography>
  </Box>
);
